import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { t, Lang } from '../../../../i18n';

// Define the task data structure - matching TaskWithDialogue properties exactly
interface TaskData {
  id: string;
  userDescription?: string;
  description?: string;
  dutchDescription?: string;
  selectedTask?: { description: string; task_id: string; similarity: number };
  selectedVariant?: any;
  selectedVariantId?: string;
  variantDescription?: string; // The variant selection text
  billableAmount?: { amount: number; unit: string }; // The billable amount with unit
  employeeCount?: number | null; // Allow null to match TaskWithDialogue
  location?: string | null; // Using 'location' instead of 'place' to match TaskWithDialogue
}

interface TaskSummaryTableProps {
  tasks: TaskData[];
  isEditable?: boolean;
  onTaskUpdate?: (taskId: string, field: string, value: any) => void;
  onTaskRemove?: (taskId: string) => void;
  lang?: Lang;
}

const columnHelper = createColumnHelper<TaskData>();

const TaskSummaryTable: React.FC<TaskSummaryTableProps> = ({ tasks, isEditable = false, onTaskUpdate, onTaskRemove, lang = 'en' }) => {


  const columns = [
    columnHelper.accessor('selectedTask', {
      header: t(lang, 'taskDescription'),
      cell: (info) => {
        const selectedTask = info.getValue();
        const taskId = info.row.original.id;
        const task = info.row.original;
        
        // Use Dutch description if language is Dutch, otherwise use English description
        const value = lang === 'nl' 
          ? (task.dutchDescription || selectedTask?.description || 'N/A')
          : (selectedTask?.description || 'N/A');
        
        if (isEditable) {
          return (
            <input
              type="text"
              defaultValue={value}
              onBlur={(e) => {
                if (onTaskUpdate && e.target.value !== value) {
                  onTaskUpdate(taskId, 'description', e.target.value);
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          );
        }
        return value;
      },
      size: 300, // Original width
    }),
    columnHelper.accessor('selectedVariantId', {
      header: t(lang, 'id'),
      cell: (info) => {
        const taskId = info.row.original.id;
        const raw = info.getValue();
        // CRITICAL: Never use info.row.original.id (UUID) as fallback - it's the React key, not the task ID
        // Check if raw value is valid (not UUID, not parent ID, not description)
        const isUUID = (s: string) => /^[a-z0-9]{8,}$/i.test(s) && s.length >= 8 && !/^\d+$/.test(s);
        const isParentId = (s: string) => s && (s.startsWith('property_') || s.startsWith('manhole_'));
        const looksLikeValidId = raw && 
                                 raw !== taskId && 
                                 !isUUID(String(raw)) && 
                                 !isParentId(String(raw)) &&
                                 /^[\dA-Za-z_-]+$/.test(String(raw)) &&
                                 String(raw).length >= 3 && 
                                 String(raw).length <= 20;
        
        // If raw is valid, use it. Otherwise try to extract from row.id (composite ID like "taskId-subtaskId")
        let value = looksLikeValidId ? raw : null;
        if (!value && info.row.original.id.includes('-')) {
          // Try to extract numeric ID from composite row ID (e.g. "abc123-164" -> "164")
          const parts = info.row.original.id.split('-');
          const numericPart = parts.find((p: string) => /^\d+$/.test(p));
          if (numericPart && !isUUID(numericPart)) {
            value = numericPart;
          } else if (parts[0] && !isUUID(parts[0]) && !isParentId(parts[0])) {
            value = parts[0];
          }
        }
        const displayValue = value ?? 'N/A';
        
        if (isEditable) {
          return (
            <input
              type="text"
              defaultValue={displayValue}
              onBlur={(e) => {
                if (onTaskUpdate && e.target.value !== displayValue) {
                  onTaskUpdate(taskId, 'selectedVariantId', e.target.value);
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          );
        }
        return displayValue;
      },
      size: 80, // Original width
    }),
    columnHelper.accessor('billableAmount', {
      id: 'billableUnit', // Unique ID for this column
      header: t(lang, 'billableUnit'),
      cell: (info) => {
        const taskId = info.row.original.id;
        const value = info.getValue();
        const unit = (value && typeof value === 'object' && 'unit' in value) ? value.unit : 'N/A';
        
        if (isEditable) {
          return (
            <input
              type="text"
              defaultValue={unit}
              onBlur={(e) => {
                if (onTaskUpdate && e.target.value !== unit) {
                  const currentAmount = value && typeof value === 'object' && 'amount' in value ? value.amount : null;
                  onTaskUpdate(taskId, 'billableAmount', { amount: currentAmount, unit: e.target.value });
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          );
        }
        return unit;
      },
      size: 80, // Original width
    }),
    columnHelper.accessor('billableAmount', {
      id: 'billableAmount', // Unique ID for this column
      header: t(lang, 'billableAmount'),
      cell: (info) => {
        const taskId = info.row.original.id;
        const value = info.getValue();
        const amount = (value && typeof value === 'object' && 'amount' in value) ? value.amount : 'N/A';
        
        if (isEditable) {
          return (
            <input
              type="number"
              step="0.01"
              defaultValue={amount}
              onBlur={(e) => {
                if (onTaskUpdate && e.target.value !== String(amount)) {
                  const currentUnit = value && typeof value === 'object' && 'unit' in value ? value.unit : 'N/A';
                  const numValue = e.target.value ? parseFloat(e.target.value) : null;
                  onTaskUpdate(taskId, 'billableAmount', { amount: numValue, unit: currentUnit });
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          );
        }
        return amount;
      },
      size: 80, // Original width
    }),
    columnHelper.accessor('employeeCount', {
      header: t(lang, 'employees'),
      cell: (info) => {
        const taskId = info.row.original.id;
        const value = info.getValue() || 'N/A';
        
        if (isEditable) {
          return (
            <input
              type="number"
              min="0"
              defaultValue={value === 'N/A' ? '' : value}
              onBlur={(e) => {
                if (onTaskUpdate) {
                  const numValue = e.target.value ? parseInt(e.target.value, 10) : null;
                  onTaskUpdate(taskId, 'employeeCount', numValue);
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          );
        }
        return value;
      },
      size: 100, // Original width
    }),
    columnHelper.accessor('location', {
      header: t(lang, 'location'),
      cell: (info) => {
        const taskId = info.row.original.id;
        const value = info.getValue() || 'N/A';
        
        if (isEditable) {
          return (
            <input
              type="text"
              defaultValue={value}
              onBlur={(e) => {
                if (onTaskUpdate && e.target.value !== value) {
                  onTaskUpdate(taskId, 'location', e.target.value);
                }
              }}
              style={{
                width: '100%',
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          );
        }
        return value;
      },
      size: 150, // Original width
    }),
    // Add delete column only when editable
    ...(isEditable ? [columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const taskId = info.row.original.id;
        return (
          <button
            onClick={() => {
              if (onTaskRemove) {
                onTaskRemove(taskId);
              }
            }}
            style={{
              padding: '4px 8px',
              backgroundColor: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444';
            }}
            title={t(lang, 'removeTask')}
          >
            ×
          </button>
        );
      },
      size: 60, // Width for delete button
    })] : []),
  ];

  const table = useReactTable({
    data: tasks,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (tasks.length === 0) {
    return <div className="text-gray-500 italic">{t(lang, 'noCompletedTasksToDisplay')}</div>;
  }

  return (
    <div className="task-summary-table">
      {/* Horizontal scrollable container for mobile */}
      <div 
        className="overflow-x-auto scrollbar-hide border border-gray-200 rounded-lg" 
        style={{ 
          maxWidth: '100%',
          width: '100%',
          overflowX: 'auto', // Force horizontal scrolling
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          scrollbarWidth: 'thin', // Thin scrollbar on Firefox
          msOverflowStyle: 'none', // Hide scrollbar on IE/Edge
          opacity: isEditable ? 1 : 0.9, // Slightly faded when "printed"
          filter: isEditable ? 'none' : 'grayscale(5%)' // Subtle grayscale when printed
        }}
      >
                                   <table 
            style={{ 
              tableLayout: 'fixed', // Use fixed layout for consistent column widths
              borderCollapse: 'collapse',
              border: '1px solid white', // Thinner white border
              width: isEditable ? '830px' : '770px', // Total width: 770px + 60px for delete column when editable
              minWidth: isEditable ? '830px' : '770px' // Ensure minimum width is maintained
            }}
          >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                                 <th
                   key={header.id}
                   className="px-4 py-3 text-left text-sm font-semibold text-gray-800 bg-gray-200"
                   style={{ 
                     width: `${header.getSize()}px`,
                     minWidth: `${header.getSize()}px`,
                     maxWidth: `${header.getSize()}px`,
                     border: '1px solid white', // Thinner white border
                     borderCollapse: 'collapse'
                   }}
                 >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={isEditable ? "hover:bg-gray-50" : ""}>
              {row.getVisibleCells().map((cell) => (
                                 <td
                   key={cell.id}
                   className="px-4 py-3 text-sm text-gray-700 bg-white"
                   style={{ 
                     width: `${cell.column.getSize()}px`,
                     minWidth: `${cell.column.getSize()}px`,
                     maxWidth: `${cell.column.getSize()}px`,
                     wordWrap: 'break-word',
                     overflowWrap: 'break-word',
                     border: '1px solid white', // Thinner white border
                     borderCollapse: 'collapse',
                     cursor: isEditable ? 'text' : 'default'
                   }}
                 >
                  <div style={{ 
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: '1.4',
                    minHeight: '2.8em', // Ensure consistent row height
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div> {/* Close horizontal scrollable container */}
    </div>
  );
};

export default TaskSummaryTable; 